import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "../../provider/authContextType";
import { Liferay } from "./liferay.js";

interface QueryParams {
  identityDocument: string;
  identityDocumentTypeId: string;
  identityDocumentChild?: string; // ahora opcional
  identityDocumentTypeIdChild?: string; // ahora opcional
}

const fetchMyUserAccount = async (
  origin: string | null,
  identityDocument: string,
  identityDocumentTypeId: string,
  token: string | null,
  identityDocumentChild?: string, // ahora opcional
  identityDocumentTypeIdChild?: string // ahora opcional
) => {
  try {
    const params: any = {
      identityDocument: identityDocument,
      identityDocumentTypeId: identityDocumentTypeId,
    };

    // Solo agregar los parámetros opcionales si tienen valor
    if (identityDocumentChild) {
      params.identityDocumentChild = identityDocumentChild;
    }
    if (identityDocumentTypeIdChild) {
      params.identityDocumentTypeIdChild = identityDocumentTypeIdChild;
    }

    const response = await axios.get(`${origin}/user/myuser`, {
      params: params,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.log(error);
  }
};

const fetchAlternateName = async (
  identityDocument: string,
  identityDocumentTypeId: string,
  origin: string | null,
  token: string | null
) => {
  try {
    const response = await axios.get(`${origin}/forgotpassword/finduser`, {
      params: {
        IdentityDocument: identityDocument,
        IdentityDocumentTypeId: identityDocumentTypeId,
      },
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Error al obtener el perfil"
    );
  }
};

const createUserAccount = async (
  newPassword: string,
  payload: any,
  origin: string
) => {
  try {
    const response = await axios.post(
      origin + "/affiliation/register",
      {
        alternateName:
          `${payload.identityDocumentTypeId}-${payload.identityDocument}`
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^0-9-]/g, ""),
        emailAddress: `${payload.identityDocumentTypeId}-${payload.identityDocument}@sanitasperu.com`,
        familyName: payload.firstSurName + " " + payload.secondSurName,
        givenName: payload.names,
        name:
          payload.names +
          " " +
          payload.firstSurName +
          " " +
          payload.secondSurName,
        password: newPassword,
        customFields: [
          {
            customValue: {
              data: `${payload.identityDocumentTypeId}`,
            },
            dataType: "Text",
            name: "typeDoc",
          },
          {
            customValue: {
              data: `${payload.identityDocument}`,
            },
            dataType: "Text",
            name: "numDoc",
          },
        ],
        userAccountContactInformation: {
          emailAddresses: [
            {
              emailAddress: payload.email,
              primary: true,
              type: "email-address",
            },
          ],
        },
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${Liferay.authToken}`,
        },
      }
    );

    if (response.status !== 204) {
      throw new Error("Error en la creación");
    }
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

const updateUserAccount = async (
  payload: any,
  contrasena: string,
  origin: string | null
) => {
  const response = await axios.put(
    `${origin}/forgotpassword/update`,
    {
      alternateName:
        `${payload.identityDocumentTypeId}-${payload.identityDocument}`
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^0-9-]/g, ""),
      emailAddress: `${payload.identityDocumentTypeId}-${payload.identityDocument}@sanitasperu.com`,
      familyName: payload.firstSurName + " " + payload.secondSurName,
      givenName: payload.names,
      name:
        payload.names +
        " " +
        payload.firstSurName +
        " " +
        payload.secondSurName,
      password: contrasena,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer `,
      },
    }
  );
  return response.data;
};

export const useFetchMyUserAccount = (
  identityDocument: string,
  identityDocumentTypeId: string,
  identityDocumentChild?: string,
  identityDocumentTypeIdChild?: string
) => {
  const { token, origin } = useAuth();
  return useQuery({
    queryKey: ["myUserAccount"],
    queryFn: () => {
      return fetchMyUserAccount(
        origin,
        identityDocument,
        identityDocumentTypeId,
        token,
        identityDocumentChild,
        identityDocumentTypeIdChild
      );
    },
    enabled: !!token,
  });
};

export const useFetchMutateUserAccounte = () => {
  const { origin, token } = useAuth();
  return useMutation({
    mutationFn: (data: QueryParams) => {
      const {
        identityDocument,
        identityDocumentTypeId,
        identityDocumentChild,
        identityDocumentTypeIdChild,
      } = data;
      return fetchMyUserAccount(
        origin,
        identityDocument,
        identityDocumentTypeId,
        token,
        identityDocumentChild,
        identityDocumentTypeIdChild
      );
    },
  });
};

export const useFetchAlternateName = () => {
  const { origin, token } = useAuth();
  return useMutation({
    mutationFn: (data: QueryParams) => {
      const { identityDocument, identityDocumentTypeId } = data;
      return fetchAlternateName(
        identityDocument,
        identityDocumentTypeId,
        origin,
        token
      );
    },
  });
};

export const useCreateUserAccount = () => {
  const { origin } = useAuth();
  return useMutation({
    mutationFn: ({
      newPassword,
      payload,
    }: {
      newPassword: string;
      payload: any;
    }) => {
      if (!origin) {
        throw new Error("Liferay token is null");
      }
      return createUserAccount(newPassword, payload, origin);
    },
  });
};

export const useUpdateUserAccount = () => {
  const { origin } = useAuth();
  return useMutation({
    mutationFn: ({
      payload,
      contrasena,
    }: {
      payload: any;
      contrasena: string;
    }) => {
      return updateUserAccount(payload, contrasena, origin);
    },
  });
};
